import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { animated, useTransition } from "@react-spring/web";
import dateformat from "dateformat";

import { SearchBar } from "src/components/input";
import { TabBar } from "src/components/tabbar";
import Slider from "src/components/slider";
import Thumbnail from "src/components/thumbnail";
import { TabSection } from "src/pages/dealRoom/pages/assets";
import { AssetCard } from "src/components/asset/add/card";
import InfiniteScroll from "src/components/infiniteScroll";

import {
  fetchPublishedPodcasts,
  fetchPublishedSimulations,
  invalidatePublishedPodcasts,
  invalidatePublishedSimulations,
  searchPublishedPodcasts,
  searchPublishedSimulations,
} from "src/redux/actions/coaching/courses";
import { CourseContext } from "../../context";
import { FileService } from "src/utils/file.service";

import _ from "lodash";
import cx from "classnames";
import styles from "./add.module.scss";
import { Podcast } from "src/types/coaching/podcast";
import { Simulation } from "src/types/coaching/simulation";
import Text from "src/components/text";

const defaultAssetImage = "/images/defaultIcons/default_asset.jpg";
const defaultPodcastImage = "/images/assets/podcastThumbnail.jpg";
const defaultSimulationImage = "/images/assets/simulationThumbnail.jpg"

interface AddLearningBrowserProps {
  width: number | string;
  className?: string;
  onClose: () => void;
  show: boolean;
}

export const AddLearningBrowser: React.FC<AddLearningBrowserProps> = ({
  className,
  onClose,
}) => {
  const [query, setQuery] = useState<string | null>("");
  const { selected, setSelected } = useContext(CourseContext);

  const browserTransition = useTransition(!query, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const searchTransition = useTransition(query, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const selectionTransition = useTransition(selected.entities.length > 0, {
    from: { width: 0, opacity: 0 },
    enter: { width: 320, opacity: 1 },
    leave: { width: 0, opacity: 0 },
  });

  const handleQueryChange = (value) => {
    setQuery(value);
  };

  const handleSelect = () => (item: any) => {
    setSelected((prev) => ({
      ...prev,
      entities: prev.entities.some((e) => e.id === item.id)
        ? prev.entities
        : [...prev.entities, item],
    }));
  };

  return (
    <div style={{ width: "380px" }} className={styles.hub}>
      <animated.div className={styles.hubAssets}>
        <Slider.Body className={styles.body}>
          <div className={styles.learningLayout}>
            {/* LEFT SIDE */}
            <div className={cx(styles.left, styles.stacks, styles.content)}>
              <div className={styles.header}>
                <div className={styles.heading}>
                  <i
                    className="paperflite-left-arrow"
                    onClick={() => onClose()}
                  />
                  <div className={styles.text}> pick from studio</div>
                </div>
                <div className={styles.subText}>
                  Choose from podcasts and simulations published in your studio
                </div>
              </div>
              <div className={cx(styles.searchBar, className)}>
                <SearchBar query={query} onSearch={handleQueryChange} />
              </div>
              {browserTransition(
                (style, item) =>
                  item && (
                    <LearningBrowser
                      style={style}
                      className={className}
                      onSelect={handleSelect()}
                    />
                  ),
              )}

              {searchTransition(
                (style, item) =>
                  item && (
                    <LearningSearchResults
                      style={style}
                      query={query}
                      onSelect={handleSelect()}
                    />
                  ),
              )}
            </div>
          </div>
        </Slider.Body>
      </animated.div>
    </div>
  );
};

const LearningBrowser: React.FC<{
  style: any;
  className?: string;
  onSelect: (item: any) => void;
}> = ({ style, className, onSelect }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (activeTab === 0) {
      dispatch(
        fetchPublishedPodcasts({
          page: 0,
          limit: 15,
          courseTypes: ["podcast"],
        }),
      );
    } else {
      dispatch(
        fetchPublishedSimulations({
          page: 0,
          limit: 15,
          courseTypes: ["simulation"],
        }),
      );
    }
  }, [activeTab]);

  return (
    <animated.div
      style={style}
      className={cx(styles.tabsContainer, styles.stack)}
    >
      <TabBar
        className={cx(styles.tabs, className)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={setActiveTab}
      >
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>
          Podcast
        </div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>
          Simulation
        </div>
      </TabBar>

      <div className={cx(styles.stacks, styles.tabContainer)} key={activeTab}>
        <TabSection index={0} selected={activeTab}>
          <div className={styles.tabBody}>
            {activeTab === 0 && (
              <PodcastBrowser
                onSelect={onSelect}
              />
            )}
          </div>
        </TabSection>

        <TabSection index={1} selected={activeTab}>
          <div className={styles.tabBody}>
            {activeTab === 1 && (
              <SimulationBrowser
                onSelect={onSelect}
              />
            )}
          </div>
        </TabSection>
      </div>
    </animated.div>
  );
};

const LearningSearchResults: React.FC<{
  style: any;
  query?: string | null;
  onSelect: (item: any) => void;
}> = ({ style, query, onSelect }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = React.useState(0);

  useEffect(() => {
    if (!query) return;

    dispatch(invalidatePublishedPodcasts());
    dispatch(invalidatePublishedSimulations());

    dispatch(
      searchPublishedPodcasts({
        page: 0,
        limit: 15,
        courseTypes: ["podcast"],
        query,
      }),
    );

    dispatch(
      searchPublishedSimulations({
        page: 0,
        limit: 15,
        courseTypes: ["simulation"],
        query,
      }),
    );
  }, [query]);

  const podcastSearchResults = useSelector(
    (state: any) => _.get(state, "courses.search.published.podcast", {})
  );
  
  const simulationSearchResults = useSelector(
    (state: any) => _.get(state, "courses.search.published.simulation", {})
  );

  const {
    items: podcastItems = [],
    loading: podcastLoading = false,
    pagination: {
      hasMoreItems: podcastHasMoreItems = false,
      currentPage: podcastCurrentPage = 0,
      totalRecordsCount: podcastTotalRecordsCount = 0,
    },
  } = podcastSearchResults;
  
  const {
    items: simulationItems = [],
    loading: simulationLoading = false,
    pagination: {
      hasMoreItems: simulationHasMoreItems = false,
      currentPage: simulationCurrentPage = 0,
      totalRecordsCount: simulationTotalRecordsCount = 0,
    },
  } = simulationSearchResults;
  

  return (
    <animated.div
      style={style}
      className={cx(styles.tabsContainer, styles.stack)}
    >
      <TabBar
        className={cx(styles.tabs)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={setActiveTab}
      >
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>
          Podcast ({podcastTotalRecordsCount})
        </div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>
          Simulation  ({simulationTotalRecordsCount})
        </div>
      </TabBar>

      <div className={cx(styles.stacks, styles.tabContainer)}>
        {activeTab === 0 && (
          <div className={styles.tabBody}>
            <SearchResultsList
              items={podcastItems}
              loading={podcastLoading}
              hasMoreItems={podcastHasMoreItems}
              defaultBanner={defaultPodcastImage}
              nextPage={podcastCurrentPage + 1}
              onSelect={onSelect}
              onLoadMore={(page) =>
                dispatch(
                  searchPublishedPodcasts({
                    page,
                    limit: 15,
                    query: query || "",
                    courseTypes: ["podcast"],
                  }),
                )
              }
              getId={(item: any) => item.id}
              getCourse={(item: any) => item.course}
              type="podcast"
            />

          </div>
        )}

        {activeTab === 1 && (
          <div className={styles.tabBody}>
            <SearchResultsList
              items={simulationItems}
              loading={simulationLoading}
              hasMoreItems={simulationHasMoreItems}
              defaultBanner={defaultSimulationImage}
              nextPage={simulationCurrentPage + 1}
              onSelect={onSelect}
              onLoadMore={(page) =>
                dispatch(
                  searchPublishedSimulations({
                    page,
                    limit: 15,
                    courseTypes: ["simulation"],
                    query: query || "",
                  }),
                )
              }
              getId={(item: any) => item.id}
              getCourse={(item: any) => item.course}
              type="simulation"
            />

          </div>
        )}
      </div>
    </animated.div>
  );
};

interface PodcastBrowserProps {
  onSelect: (course: Podcast) => void;
}

export const PodcastBrowser: React.FC<PodcastBrowserProps> = ({
  onSelect
}) => {
  const dispatch = useDispatch();
  const { selected } = React.useContext(CourseContext);
  const { podcasts, loading, hasMoreItems, nextPage } = useSelector(
    (state: any) => {
      const podcastState = _.get(state, "courses.podcast");

      const podcasts = _.get(podcastState, "items") || [];

      const loading = _.get(podcastState, "loading") || false;

      const pagination = _.get(podcastState, "pagination");

      const hasMoreItems = _.get(pagination, "hasMoreItems", false);

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;

      if (!hasMoreItems) {
        nextPage = null;
      }

      return {
        podcasts,
        loading,
        hasMoreItems,
        nextPage,
      };
    },
    _.isEqual,
  );

  const fetchPodcastsForPage = (page: number): void => {
    dispatch(
      fetchPublishedPodcasts({
        courseTypes: ["podcast"],
        page,
        limit: 15,
      }),
    );
  };

  const handleLoadMorePodcasts = () => {
    if (nextPage !== null && !loading) {
      fetchPodcastsForPage(nextPage);
    }
  };

  const onAddPodcast = (course: Podcast) => {
    if (!selected?.entities?.some((item) => item.id === course.id)) {
      onSelect(course);
    }
  };

  return (
    <>
      <InfiniteScroll
        className={styles.courseList}
        hasMore={hasMoreItems}
        loading={loading}
        loadMore={handleLoadMorePodcasts}
        loader={() => <UnitPlaceholder />}
      >
        {podcasts.map((course: any) => (
          <div
            key={course.id}
            className={cx(styles.podcast)}
            onClick={() => onAddPodcast(course)}
          >
            <Thumbnail
              media={course.course.banner?.background}
              defaultImage={defaultPodcastImage}
              className={styles.thumbnail}
            />

            <div className={styles.details}>
              <div className={styles.detail}>
                <div className={styles.name}>
                  <Text text={course?.course?.name || "Untitled podcast"} lines={1} tooltip={{placement: "right"}} /> 
                </div>

                <div className={styles.metadata}>
                  <div>
                    {FileService.formatDuration(course?.course?.duration, "long")}
                  </div>
                  <div className={styles.divider} />
                  <div>
                    last updated:{" "}
                    {dateformat(course?.course?.modifiedDate, "dd mmm yyyy")}
                  </div>
                </div>
              </div>
              <div className={styles.add}>
                <i className="paperflite-add" />
              </div>
            </div>
          </div>
        ))}
      </InfiniteScroll>
    </>
  );
};

interface SimulationBrowserProps {
  onSelect: (course: Simulation) => void;
}

const SimulationBrowser: React.FC<SimulationBrowserProps> = ({
  onSelect,
}) => {
  const { selected } = React.useContext(CourseContext);
  const dispatch = useDispatch();

  const { simulations, loading, hasMoreItems, nextPage, total } = useSelector(
    (state: any) => {
      const simulationState = _.get(state, "courses.simulation");

      const simulations = _.get(simulationState, "items") || [];

      const loading = _.get(simulationState, "loading") || false;

      const pagination = _.get(simulationState, "pagination");

      const total = _.get(pagination, "totalRecordsCount", 0);

      const hasMoreItems = _.get(pagination, "hasMoreItems", false);

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;

      if (!hasMoreItems) {
        nextPage = null;
      }

      return {
        simulations,
        loading,
        hasMoreItems,
        nextPage,
        total,
      };
    },
    _.isEqual,
  );

  const fetchSimulationsForPage = (page: number): void => {
    dispatch(
      fetchPublishedSimulations({
        courseTypes: ["simulation"],
        page,
        limit: 15,
      }),
    );
  };

  const handleLoadMoreSimulations = () => {
    if (nextPage !== null && !loading) {
      fetchSimulationsForPage(nextPage);
    }
  };

  const onSimulationAdd = (course:Simulation) => {
    if (!selected?.entities?.some((item) => item.id === course.id)) {
      onSelect(course);
    }
  };

  return (
    <>
      <InfiniteScroll
        className={styles.list}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreSimulations}
        loader={() => <UnitPlaceholder />}
      >
        {simulations.map((course: any) => (
          <div
            key={course.id}
            className={cx(styles.simulation)}
            onClick={() => onSimulationAdd(course)}
          >
            <Thumbnail
              media={course.course.banner?.background}
              defaultImage={defaultSimulationImage}
              className={styles.thumbnail}
            />

            <div className={styles.details}>
              <div className={styles.detail}>
                <div className={cx(styles.name)}>
                  <Text text={course?.course?.name || "Untitled simulation"} lines={1} tooltip={{placement: "right"}} /> 
                </div>

                <div className={styles.metadata}>
                  <div>
                    {course?.course?.duration} Minutes
                  </div>
                  <div className={styles.divider} />
                  <div>
                    last updated:{" "}
                    {dateformat(course?.course?.modifiedDate, "dd mmm yyyy")}
                  </div>
                </div>
              </div>
              <div className={styles.add}>
                <i className="paperflite-add" />
              </div>
            </div>
          </div>
        ))}
      </InfiniteScroll>
    </>
  );
};

const UnitPlaceholder: React.FC = () => {
  return (
    <div className={styles.container}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={styles.card}>
          <div className={styles.thumbnail} />
          <div className={styles.content}>
            <div className={styles.main} />
            <div className={styles.sub} />
          </div>
        </div>
      ))}
    </div>
  );
};

interface SearchResultsListProps<T> {
  items: T[];
  loading: boolean;
  defaultBanner: string;
  hasMoreItems: boolean;
  nextPage: number | null;
  onSelect: (item: T) => void;
  onLoadMore: (page: number) => void;
  getId: (item: T) => string;
  getCourse: (item: T) => any;
  type: string;
}

export const SearchResultsList = <T,>({
  items,
  loading,
  defaultBanner,
  hasMoreItems,
  nextPage,
  onSelect,
  onLoadMore,
  getId,
  getCourse,
  type
}: SearchResultsListProps<T>) => {

  const { selected } = React.useContext(CourseContext);

  const handleLoadMore = () => {
    if (nextPage !== null && !loading) {
      onLoadMore(nextPage);
    }
  };

  const handleAdd = (item: T) => {
    const course = getCourse(item);
    if (!selected?.entities?.some((entity) => entity.id === course.id)) {
      onSelect(item);
    }
  };

  const handleClick = (item: T) => {
    handleAdd(item);
  };

  if (!loading && items.length === 0) {
    return <div className={styles.noRecordsFound}>No records found</div>;
  }

  return (
    <InfiniteScroll
      className={styles.list}
      hasMore={hasMoreItems}
      loading={loading}
      loadMore={handleLoadMore}
      loader={() => <UnitPlaceholder />}
    >
      {items.map((item) => {
        const course = getCourse(item);

        return (
          <div
            key={getId(item)}
            className={cx(styles.course, styles[type],
            )}
            onClick={() => handleClick(item)}
          >
            <Thumbnail
              media={course?.banner?.background}
              defaultImage={defaultBanner}
              className={styles.thumbnail}
            />

            <div className={styles.details}>
              <div className={styles.detail}>
                <div className={styles.name}>
                  <Text
                    text={course?.name || "Untitled"}
                    lines={1}
                    tooltip={{ placement: "right" }}
                  />
                </div>

                <div className={styles.metadata}>
                  <div>
                    {FileService.formatDuration(course?.duration, "long")}
                  </div>
                  <div className={styles.divider} />
                  <div>
                    last updated:{" "}
                    {dateformat(course?.modifiedDate, "dd mmm yyyy")}
                  </div>
                </div>
              </div>
                  <div className={styles.add}>
                  <i className="paperflite-add" />
                </div>
            </div>
          </div>
        );
      })}
    </InfiniteScroll>
  );
};
